import * as React from 'react';
import { observer } from 'mobx-react';
import * as classNames from 'classnames';
import * as moment from 'moment';
import { toFixed, unformat } from 'accounting';
import { ContentEditable } from 'components/content';
import { IconButton } from 'components/action';
import { SavingIndicator, SavingIndicatorStatus } from 'components/indicator';
import {
  INewPriceItem,
  StatusOfNewPricingRequest,
  IPricingRequestDetails,
  IGroupPriceItem,
} from 'common/interfaces/pricing';
import { computed } from 'mobx';

// Styles
const s = require('./PricingRequestContent.css');
const removeIcon = require('assets/close orange icon.svg');

// Props
interface IPricingContentProps {
  onItemRemoveFromPricingRequest: (itemId: number) => void;
  onRequestNoteChange: (noteHtml: string, noteText: string) => void;
  onItemRequestedPriceChange: (itemId: number, price: string) => void;
  onItemRequestedQuantityChange: (itemId: number, quantity: string) => void;
  onItemApprovedPriceChange: (
    itemId: number,
    price: string,
    onlyListItems?: boolean
  ) => void;
  onItemGroupPriceChange: (itemId: number, price: string) => void;
  onSubmitRequest: () => void;
  onApproveRequest: () => void;
  onSaveRequest: () => void;
  onUpdatePrices: () => void;
  existingRequest?: IPricingRequestDetails;
  userCanApprovePricing?: boolean;
  onlyListItems: boolean;
  new: boolean;
  items: INewPriceItem[];
  groupItems: IGroupPriceItem[];
  noteHtml: string;
  status: StatusOfNewPricingRequest;
}

// Component
@observer
export class PricingRequestContent extends React.Component<
  IPricingContentProps,
  {}
> {
  static get defaultProps(): IPricingContentProps {
    return {
      onItemRemoveFromPricingRequest: null,
      onRequestNoteChange: null,
      onItemRequestedPriceChange: null,
      onItemRequestedQuantityChange: null,
      onItemApprovedPriceChange: null,
      onItemGroupPriceChange: null,
      onSubmitRequest: null,
      onApproveRequest: null,
      onSaveRequest: null,
      onUpdatePrices: null,
      existingRequest: null,
      userCanApprovePricing: false,
      onlyListItems: false,
      new: false,
      items: [],
      groupItems: [],
      noteHtml: '',
      status: StatusOfNewPricingRequest.None,
    };
  }

  @computed private get notAllItemsHaveApprovedPrice(): boolean {
    const { existingRequest } = this.props;

    if (existingRequest !== null && existingRequest.item.length > 0) {
      return existingRequest.item.some(
        (item) =>
          item.approvedPrice === null || item.approvedPrice.trim() === ''
      );
    } else {
      return false;
    }
  }

  @computed private get showGroupPrice(): boolean {
    return (
      this.props.existingRequest !== null &&
      this.props.existingRequest.providerGroup !== null
    );
  }

  public render() {
    const {
      existingRequest,
      onlyListItems,
      userCanApprovePricing,
    } = this.props;

    return (
      <div className={s.container}>
        {this.props.userCanApprovePricing &&
          this.props.new === false &&
          existingRequest !== null && (
            <div className={s.details}>
              <div className={s.row}>
                {(typeof existingRequest.provider !== 'undefined' &&
                  existingRequest.provider && (
                    <h4 className={s.lead}>
                      {typeof existingRequest.provider.dba !== 'undefined' &&
                        existingRequest.provider.dba && (
                          <strong>{existingRequest.provider.dba}</strong>
                        )}
                      {typeof existingRequest.provider.legalBusName !==
                        'undefined' &&
                        existingRequest.provider.legalBusName && (
                          <em>{existingRequest.provider.legalBusName}</em>
                        )}
                    </h4>
                  )) || (
                  <h4 className={s.lead}>
                    <strong>{existingRequest.providerId}</strong>
                  </h4>
                )}

                <p className={s.author}>
                  {!onlyListItems &&
                    typeof existingRequest.createdAt !== 'undefined' &&
                    existingRequest.createdAt && (
                      <span>
                        {moment(existingRequest.createdAt).format(
                          'MMM Do, YYYY h:mm A'
                        )}
                      </span>
                    )}

                  {!onlyListItems &&
                    (typeof existingRequest.createdByUser !== 'undefined' ||
                      typeof existingRequest.createdByUserId !==
                        'undefined') && (
                      <span>
                        by{' '}
                        {typeof existingRequest.createdByUser !== 'undefined' &&
                        typeof existingRequest.createdByUser.name !==
                          'undefined'
                          ? existingRequest.createdByUser.name
                          : typeof existingRequest.createdByUserId !==
                            'undefined'
                          ? existingRequest.createdByUserId.toString()
                          : ''}
                      </span>
                    )}
                </p>
              </div>
            </div>
          )}

        <div className={s.header}>
          <div className={s.row}>
            <div>Item</div>
            <div>List price</div>
            {!onlyListItems && <div>Approved price</div>}
            {!onlyListItems && <div>Requested price</div>}
            {!onlyListItems && <div>Monthly usage</div>}
            {onlyListItems && <div>Customer price</div>}
            {this.showGroupPrice && onlyListItems && <div>Group price</div>}
          </div>
        </div>

        {this.props.items.length > 0 && (
          <div className={s.items}>
            {this.props.items.map((item, i) => {
              let groupPrice: string | null = null;
              this.props.groupItems.some((group) => {
                if (item.itemcode === group.itemcode) {
                  groupPrice =
                    typeof group.price === 'string'
                      ? group.price.trim()
                      : group.price;
                  return true;
                }
              });

              return (
                <div key={item.id} className={s.row}>
                  <div>
                    {this.props.new && (
                      <IconButton
                        className={s.removeButton}
                        onClick={this.handleItemRemoveFromNewRequest(item.id)}
                        label="Remove"
                        icon={removeIcon}
                      />
                    )}
                    <p>
                      <strong>{item.itemcode}</strong>
                      <span>{item.itemname}</span>
                    </p>
                  </div>

                  <div>
                    <p>
                      <span>$</span>
                      <strong>{toFixed(item.price, 2)}</strong>
                    </p>
                  </div>

                  {((!onlyListItems ||
                    (onlyListItems && this.props.userCanApprovePricing)) && (
                    <div>
                      <input
                        placeholder={
                          typeof item.approvedPrice !== 'undefined' &&
                          item.approvedPrice === null
                            ? 'None'
                            : 'Price'
                        }
                        onChange={this.handleApprovedPriceChange(item.id)}
                        onBlur={this.handleApprovedPriceChangeFormatting(
                          item.id
                        )}
                        value={
                          typeof item.approvedPrice !== 'undefined' &&
                          item.approvedPrice !== null
                            ? typeof item.approvedPrice === 'number'
                              ? toFixed(item.approvedPrice, 2)
                              : item.approvedPrice
                            : ''
                        }
                      />
                    </div>
                  )) ||
                    ((onlyListItems ||
                      this.props.new ||
                      (existingRequest !== null && existingRequest.complete) ||
                      this.props.userCanApprovePricing === false) && (
                      <div>
                        {(typeof item.approvedPrice !== 'undefined' &&
                          item.approvedPrice !== null && (
                            <p>
                              <span>$</span>
                              <strong>
                                {toFixed(unformat(item.approvedPrice), 2)}
                              </strong>
                            </p>
                          )) || (
                          <p>
                            <span>None</span>
                          </p>
                        )}
                      </div>
                    ))}

                  {(this.showGroupPrice &&
                    onlyListItems &&
                    userCanApprovePricing && (
                      <div>
                        <input
                          placeholder={groupPrice === null ? 'None' : 'Price'}
                          onChange={this.handleApprovedGroupPriceChange(
                            item.id
                          )}
                          onBlur={this.handleApprovedGroupPriceChangeFormatting(
                            item.id
                          )}
                          value={
                            groupPrice !== null
                              ? typeof groupPrice === 'number'
                                ? toFixed(groupPrice, 2)
                                : groupPrice
                              : ''
                          }
                        />
                      </div>
                    )) ||
                    (this.showGroupPrice && onlyListItems && (
                      <div>
                        {(groupPrice !== null && groupPrice !== '' && (
                          <p>
                            <span>$</span>
                            <strong>
                              {typeof groupPrice === 'number'
                                ? toFixed(groupPrice, 2)
                                : groupPrice}
                            </strong>
                          </p>
                        )) || (
                          <p>
                            <span>None</span>
                          </p>
                        )}
                      </div>
                    ))}

                  {!onlyListItems && (
                    <div>
                      {(this.props.new && (
                        <input
                          placeholder="Price"
                          onChange={this.handleRequestedPriceChange(item.id)}
                          onBlur={this.handleRequestedPriceChangeFormatting(
                            item.id
                          )}
                          value={
                            item.expectedPrice !== null
                              ? item.expectedPrice
                              : ''
                          }
                        />
                      )) || (
                        <p>
                          <span>$</span>
                          <strong>
                            {toFixed(unformat(item.expectedPrice), 2)}
                          </strong>
                        </p>
                      )}
                    </div>
                  )}

                  {!onlyListItems && (
                    <div>
                      {(this.props.new && (
                        <input
                          placeholder="Quantity"
                          onChange={this.handleRequestedQuantityChange(item.id)}
                          value={item.volume !== null ? item.volume : ''}
                        />
                      )) || (
                        <p>
                          <strong>{toFixed(unformat(item.volume), 0)}</strong>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {this.props.items.length <= 0 && !onlyListItems && (
          <div className={s.empty}>
            <p>
              No items added to pricing request. Start by searching for items
              client has requested.
            </p>
          </div>
        )}

        {(((this.props.items.length > 0 && !onlyListItems) ||
          (this.props.userCanApprovePricing &&
            onlyListItems &&
            existingRequest !== null)) && (
          <div className={s.footer}>
            <div className={s.row}>
              {(!onlyListItems && (
                <ContentEditable
                  classNameForField={classNames(s.note, {
                    [s.nonEditable]: !this.props.new,
                  })}
                  contentEditable={this.props.new}
                  autoFocus={false}
                  selectTextOnFirstFocus={false}
                  placeholder={
                    this.props.new
                      ? 'Pricing request notes...'
                      : 'No note attached by request\'s author...'
                  }
                  html={this.props.noteHtml}
                  onChange={this.handleDescriptionChange}
                />
              )) || <div className={classNames(s.note, s.noteUnavailable)} />}

              {((this.props.new ||
                this.props.userCanApprovePricing ||
                (this.props.userCanApprovePricing &&
                  ((existingRequest !== null &&
                    existingRequest.approvedPricesModified) ||
                    (existingRequest !== null &&
                      existingRequest.groupPricesModified)))) && (
                <div className={s.submit}>
                  <button className="boxButton" onClick={this.handleSubmit}>
                    {existingRequest !== null &&
                    this.props.userCanApprovePricing &&
                    onlyListItems
                      ? 'Update prices'
                      : this.props.new
                      ? 'Submit request'
                      : this.notAllItemsHaveApprovedPrice
                      ? 'Save pricing request'
                      : 'Approve pricing request'}
                  </button>

                  <SavingIndicator
                    align="right"
                    labels={{
                      note:
                        this.notAllItemsHaveApprovedPrice && !onlyListItems
                          ? 'Not all items have approved price entered. ' +
                            'To close pricing request you need to provide approved price for all requested items.'
                          : '',
                      success: 'Saved',
                      saving: 'Saving...',
                      error:
                        this.props.status ===
                        StatusOfNewPricingRequest.ErrorNotAllItemsHaveQuantityAndPrice
                          ? this.props.new
                            ? 'All items need price and volume defined'
                            : 'All items need approved price defined'
                          : 'Something went wrong, try again',
                    }}
                    status={
                      this.props.status === StatusOfNewPricingRequest.None
                        ? SavingIndicatorStatus.None
                        : this.props.status === StatusOfNewPricingRequest.Saving
                        ? SavingIndicatorStatus.Saving
                        : this.props.status ===
                          StatusOfNewPricingRequest.Success
                        ? SavingIndicatorStatus.Success
                        : SavingIndicatorStatus.Error
                    }
                  />
                </div>
              )) || (
                <div className={s.submit}>
                  <p className={s.statusMessage}>
                    {existingRequest !== null && existingRequest.complete
                      ? 'Request approved'
                      : 'Request pending'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )) ||
          (onlyListItems && (
            <div>
              <br />
              <br />
              <br />
            </div>
          ))}
      </div>
    );
  }

  private handleItemRemoveFromNewRequest = (itemId: number) => (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    this.props.onItemRemoveFromPricingRequest(itemId);
  };

  private handleApprovedPriceChange = (itemId: number) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    this.props.onItemApprovedPriceChange(
      itemId,
      e.currentTarget.value,
      this.props.onlyListItems
    );
  };

  private handleApprovedPriceChangeFormatting = (itemId: number) => (
    e: React.FocusEvent<HTMLInputElement>
  ) => {
    this.props.onItemApprovedPriceChange(
      itemId,
      toFixed(unformat(e.currentTarget.value), 2),
      this.props.onlyListItems
    );
  };

  private handleApprovedGroupPriceChange = (itemId: number) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    this.props.onItemGroupPriceChange(itemId, e.currentTarget.value);
  };

  private handleApprovedGroupPriceChangeFormatting = (itemId: number) => (
    e: React.FocusEvent<HTMLInputElement>
  ) => {
    this.props.onItemGroupPriceChange(
      itemId,
      toFixed(unformat(e.currentTarget.value), 2)
    );
  };

  private handleRequestedPriceChange = (itemId: number) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    this.props.onItemRequestedPriceChange(itemId, e.currentTarget.value);
  };

  private handleRequestedPriceChangeFormatting = (itemId: number) => (
    e: React.FocusEvent<HTMLInputElement>
  ) => {
    this.props.onItemRequestedPriceChange(
      itemId,
      toFixed(unformat(e.currentTarget.value), 2)
    );
  };

  private handleRequestedQuantityChange = (itemId: number) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    this.props.onItemRequestedQuantityChange(itemId, e.currentTarget.value);
  };

  private handleDescriptionChange = (html: string, text: string) => {
    this.props.onRequestNoteChange(html, text);
  };

  private handleSubmit = () => {
    if (this.props.new) {
      this.props.onSubmitRequest();
    } else if (this.props.userCanApprovePricing) {
      if (this.props.onlyListItems) {
        this.props.onUpdatePrices();
      } else if (this.notAllItemsHaveApprovedPrice) {
        this.props.onSaveRequest();
      } else {
        this.props.onApproveRequest();
      }
    }
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/routes/sales/Lead/PricingRequest/Modal/PricingRequestContent.tsx